import {
  GalleryViewModel,
  ImageViewModel,
  MessagingCardViewModel,
  ParagraphTitleViewModel,
  ParagraphViewModel,
  PrimaryPageMessagingModel,
  RatePlanLoyaltyModel,
  RegionViewModel,
  ShortlistModel,
} from 'models';
import { StructureType } from '__generated__/api/types';

import { UitkLayoutGridColumnRow } from 'uitk-react-layout-grid';

export interface HotelDetailsViewModel {
  additionalInfo?: PropertyAdditionalInfoViewModel;
  amenities?: AllAmenitiesViewModel;
  changeSearchDisplayOptions?: any;
  defaultData?: any;
  detailsPageTitle?: string;
  englishHotelName?: string;
  error?: any;
  gallery?: GalleryViewModel;
  hotelId: number;
  hotelName?: string;
  hotelOverviewA11yHeader?: string;
  id: number;
  omnitureData?: string;
  overview?: OverviewViewModel;
  primaryPageMessagingModel?: PrimaryPageMessagingModel;
  propertyManagerViewModel?: PropertyManagerViewModel;
  propertySummaryDescription?: string;
  questionAnswersViewModel?: QuestionAnswersViewModel;
  region?: RegionViewModel;
  shortlistModel?: ShortlistModel;
  specialMessageCard?: HotelMessagingResult;
  structure?: StructureType | undefined;
  vipMessage?: string;
  lodgingType?: string;
  cleaningAndSafety?: CleaningAndSafetyPracticesViewModel;
}

export interface HotelMessagingResult {
  resultType?: string;
  model?: MessagingCardViewModel;
}

export interface AllAmenitiesViewModel {
  highlightAmenities?: HighlightAmenitiesViewModel[];
  property?: AmenityViewModel[];
  room?: AmenityViewModel[];
  top?: AmenityViewModel[];
  topSummary?: AmenityViewModel[];
}

export interface HighlightAmenitiesViewModel {
  label: string;
  sections?: AmenityViewModel[];
  title?: string;
}

export interface AmenityViewModel {
  additionalInformation?: string;
  category?: string; // enum class AmenityCategory
  description?: string;
  icon?: string;
  mark?: string;
  id?: number;
  subDescription?: string;
  headerText?: string;
}

export interface CleaningAndSafetyPracticesViewModel {
  heading?: string;
  items: CleaningAndSafetyPractice[];
  jumpLink?: CleaningAndSafetyPracticeJumpLink;
}

export interface CleaningAndSafetyPractice {
  icon?: CleaningAndSafetyPracticeIcon;
  text: string;
}

export interface CleaningAndSafetyPracticeJumpLink {
  sectionName: string;
  localizedName: string;
  icon?: CleaningAndSafetyPracticeJumpLinkIcon;
}

export interface CleaningAndSafetyPracticeIcon {
  id: string;
}
export interface CleaningAndSafetyPracticeJumpLinkIcon {
  id: string;
  description?: string;
}

export interface MarketingSectionViewModel {
  sectionTitle?: string;
  marketingItems?: AmenityViewModel[];
}

export interface AmenityBreakDownViewModel {
  excluded?: AmenityViewModel[];
  included?: AmenityViewModel[];
}

export interface RatePlanAmenityListViewModel {
  amenities?: AmenityViewModel[];
  icon?: string;
  idPrefix?: string;
  ratePlanLoyaltyModel?: RatePlanLoyaltyModel;
  triggerDialogId: string;
  amenitiesSpacing?: string;
  useMiniDialog?: boolean;
  marketingFeeInfoDialogRfrr?: string;
  dataStid?: string;
  columns?: UitkLayoutGridColumnRow;
}

export interface RatePlanMarketingInfoViewModel extends RatePlanAmenityListViewModel {
  sectionTitle?: string;
}

export interface OverviewViewModel {
  addressLine?: string;
  latitude?: string;
  longitude?: string;
  preview?: string;
  staticMapSrc?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  countryCode?: string;
  regionId?: string;
}

export interface PropertyAdditionalInfoViewModel {
  hrefNextPage: string;
  petsPolicy?: PropertyInfoSubSectionViewModel;
  primaryPropertyMessaging?: PropertyInfoSubSectionViewModel;
  propertyAlternativeName?: PropertyAlternativeNameModel;
  secondaryPropertyMessaging?: PropertySecondaryInfoViewModel;
  shouldMention?: PropertyInfoSubSectionViewModel;
  propertyAmenitiesSection: PropertyInfoSectionViewModel;
  roomAmenitiesSection: PropertyInfoSectionViewModel;
  nearbyThingsSection: PropertyInfoSectionViewModel;
  propertyDescriptionSection?: PropertyInfoSectionViewModel;
  renovationsAndClosuresSection?: PropertyInfoSectionViewModel;
  allInclusiveSection?: PropertyInfoSectionViewModel;
  awardsAndAffiliationsSection?: PropertyInfoSectionViewModel;
  propertyManagerSection?: PropertyInfoSectionViewModel;
  entirePlaceSection?: PropertyInfoSectionViewModel;
  nationalRatingSection?: PropertyInfoSubSectionViewModel;
}

export interface PropertyAlternativeNameModel {
  sections: AlternativeNames[];
  title: string;
}

export interface AlternativeNames {
  name: string;
  value: string;
}

export interface PropertyInfoSectionViewModel {
  sections: PropertyInfoSubSectionViewModel[];
  title: string;
  formattedText?: FormattedViewModel;
  image?: ImageViewModel;
  dataLabel?: string;
  toggleHidden?: ToggleAttributesModel;
}

export interface PropertyInfoSubSectionViewModel {
  body?: string[];
  descriptions?: string[];
  title: string;
  description?: string;
  formattedDescriptions?: FormattedViewModel[];
  formattedDescription?: FormattedViewModel;
  icon?: string;
}

export interface ToggleAttributesModel {
  mobile: boolean;
  desktop: boolean;
  analyticsId: string;
}

export interface FormattedViewModel {
  description: string;
  sectionType?: SectionType;
  textType: TextType;
}

export enum SectionType {
  BULLETED_LIST = 'BULLETED_LIST',
  PLAIN_LIST = 'PLAIN_LIST',
}

export enum TextType {
  HTML_TEXT = 'HTML_TEXT',
  PLAIN_TEXT = 'PLAIN_TEXT',
}

export interface PropertySecondaryInfoViewModel {
  description: string;
  heading: string;
}

export interface PropertyInfoViewModel {
  importantInfo?: PropertyInfoSectionViewModel;
  policies?: PropertyInfoPolicyViewModel;
}

export interface PropertyInfoPolicyViewModel {
  info: HotelPolicyViewModel;
  title?: string;
}

export interface HotelPolicyViewModel {
  checkinInstructions?: ParagraphViewModel;
  childBeds?: ParagraphViewModel;
  paymentOptions?: PaymentOptionViewModel[];
  houseRules?: ParagraphTitleViewModel;
  checkinPolicies?: ParagraphViewModel;
  checkoutPolicies?: ParagraphViewModel;
  accessMethodPolicies?: ParagraphViewModel;
}

export interface PaymentOptionViewModel {
  imageUrl?: string;
  name: string;
}

export interface VrPropertySummaryViewModel {
  bedroomCountText: string;
  bathroomCountText: string;
  sleepGuestCountText: string;
}

export interface PropertyManagerViewModel {
  title: string;
  name: string;
  languagesSpoken?: string;
  photo?: ImageViewModel;
}

export interface QuestionAnswersViewModel {
  enabledForConventionalLodging?: boolean; // Abacus: 30083_PWA_HIS_QUESTIONS_ANSWERS_TOP_DESTINATIONS
  enabledForHomeAway?: boolean; // Abacus: 30161_PWA_HIS_HOMEAWAY_INQUIRY
}
